'use client';
import { datadogRum } from '@datadog/browser-rum';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, TableBody, TableHead, Typography } from '@mui/material';
import useScrollToView from '@repo/hooks/dom/useScrollToView';
import { type CSSProperties, startTransition, useEffect, useRef, useState } from 'react';
import { ProductListData } from '../../../LinkedDataJson/ProductListData';
import type { PromotionalBannerSignUpOptions } from '../../../PromotionalBanner/types';
import { PromotionalButton } from '../../../PromotionalButton/PromotionalButton';
import { isMultiProductBannerEnabledForSlug } from '../MultiProductBanner/utils';
import SummaryTableContainer from './SummaryTableContainer';
import SummaryTableHead from './SummaryTableHead';
import SummaryTableRow from './SummaryTableRow';
import { SkeletonRow } from './SummaryTableRow/SkeletonRow';
import { SummaryTableSeeMoreAccountsButton } from './SummaryTableSeeMoreAccountsButton';
import { type SummaryTableRowWithDrivers } from './types';

type Props = {
  headings: { label?: string; labelTooltip?: string }[];
  rows: SummaryTableRowWithDrivers[];
  maxRows?: number;
  numTruncatedRows?: number;
  allowTruncatingRows?: boolean;
  showSkeleton?: boolean;
  slug?: string;
  pageUrl: string;
  isFilterActive?: boolean;
  allowTruncationWithFilters?: boolean;
  isAccordionActiveButtonActive?: boolean;
  shouldApplyStylingUpdates?: boolean;
  suppressApyTermPrefix?: boolean;
  isImproveQualityTrafficExperimentActive?: boolean;
  shouldDisplayWealthFrontModal?: boolean;
  shouldDisplayPromoButton?: boolean;
  signUpOptions?: PromotionalBannerSignUpOptions;
};

export const SummaryTable = ({
  headings,
  rows,
  maxRows,
  numTruncatedRows,
  allowTruncatingRows,
  showSkeleton = false,
  slug,
  pageUrl,
  isFilterActive,
  allowTruncationWithFilters,
  isAccordionActiveButtonActive,
  shouldApplyStylingUpdates = false,
  suppressApyTermPrefix = false,
  isImproveQualityTrafficExperimentActive = false,
  shouldDisplayWealthFrontModal = false,
  shouldDisplayPromoButton = false,
  signUpOptions,
}: Props) => {
  const isMultiProductBannerEnabled = isMultiProductBannerEnabledForSlug(slug || '');
  const rowsToShow = isMultiProductBannerEnabled ? 4 : numTruncatedRows;
  const [rowsAreTruncated, setRowsAreTruncated] = useState(true);
  const truncationEnabled =
    (isMultiProductBannerEnabled ||
      isAccordionActiveButtonActive ||
      (allowTruncatingRows && maxRows && rows.length > maxRows)) &&
    (!isFilterActive || allowTruncationWithFilters);
  const visibleRows = truncationEnabled && rowsAreTruncated ? rows.slice(0, rowsToShow) : rows;

  const hasSpotlightRow = rows.some(row => row.spotlightHeading && row.spotlightDetails);
  const hasMoreRowsThanVisibleCap = rowsToShow != null && rows.length > rowsToShow;
  const showTruncateButton = truncationEnabled && (hasMoreRowsThanVisibleCap || !rowsAreTruncated);

  const firedSummaryTableShown = useRef(false);

  useEffect(() => {
    if (!showSkeleton && !firedSummaryTableShown.current) {
      datadogRum.addTiming('summary_table_shown');
      firedSummaryTableShown.current = true;
    }
  }, [showSkeleton]);

  const viewMoreLessButtonHandler = () => {
    startTransition(() => {
      setRowsAreTruncated(t => !t);
    });

    if (!rowsAreTruncated) {
      useScrollToView('summary-table-headers', 'smooth', 0);
    }
  };

  const label = rowsAreTruncated ? 'See more accounts' : 'See less accounts';
  const Icon = rowsAreTruncated ? ExpandMoreIcon : ExpandLessIcon;

  return (
    <>
      <SummaryTableContainer>
        <TableHead
          id="summary-table-headers"
          sx={{
            backgroundColor: 'transparent',
            ...(hasSpotlightRow && { position: 'relative', zIndex: 1 }),
          }}
        >
          <SummaryTableHead
            headings={headings}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
          />
        </TableHead>
        <TableBody>
          {visibleRows.map((row, index) =>
            showSkeleton ? (
              <SkeletonRow
                key={`skeleton-${index}`}
                driversLength={row.marketplaceEntity.drivers.length}
              />
            ) : (
              <SummaryTableRow
                key={index}
                index={index}
                row={row}
                shouldApplyStylingUpdates={shouldApplyStylingUpdates}
                suppressApyTermPrefix={suppressApyTermPrefix}
                isImproveQualityTrafficExperimentActive={isImproveQualityTrafficExperimentActive}
                shouldDisplayWealthFrontModal={shouldDisplayWealthFrontModal}
              />
            ),
          )}
        </TableBody>
      </SummaryTableContainer>
      {showTruncateButton &&
        (isMultiProductBannerEnabled || isAccordionActiveButtonActive ? (
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              alignItems: 'center',
              justifyContent: {
                xs: 'center',
                md: 'var(--s-table-more-jc-md)',
              },
              flexDirection: 'row',
            }}
            style={
              {
                '--s-table-more-jc-md': shouldDisplayPromoButton ? 'space-between' : 'center',
              } as CSSProperties
            }
          >
            <PromotionalButton isEnabled={shouldDisplayPromoButton} signUpOptions={signUpOptions} />
            <SummaryTableSeeMoreAccountsButton
              label={label}
              icon={Icon}
              onClick={viewMoreLessButtonHandler}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: {
                xs: 'center',
                md: 'var(--s-table-more-jc-md)',
              },
              flexDirection: { xs: 'column', md: 'row' },
            }}
            style={
              {
                '--s-table-more-jc-md': shouldDisplayPromoButton ? 'space-between' : 'center',
              } as CSSProperties
            }
          >
            <PromotionalButton isEnabled={shouldDisplayPromoButton} signUpOptions={signUpOptions} />
            <Button
              variant="tertiary"
              component="span"
              disableRipple
              disableFocusRipple
              sx={{ mt: 1 }}
              onClick={viewMoreLessButtonHandler}
            >
              <Typography
                variant="accent"
                sx={{
                  fontWeight: 500,
                }}
              >
                {rowsAreTruncated ? 'View more' : 'View fewer'}
              </Typography>
            </Button>
          </Box>
        ))}
      {!showTruncateButton && shouldDisplayPromoButton && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PromotionalButton
            isEnabled={shouldDisplayPromoButton}
            signUpOptions={signUpOptions}
            variant="solo"
          />
        </Box>
      )}
      <ProductListData
        id="product-list-linked-data"
        offers={visibleRows}
        url={pageUrl}
      />
    </>
  );
};
